import { Suspense, useRef } from 'react'
import 'devextreme-react/lookup'
import 'devextreme-react/text-area'
import { useLoaderData, Await } from 'react-router-typesafe'
import { clientEditorRouteLoader } from '@/routes/clients/client/ClientEditor.route'
import ClientEditorForm from '@/routes/clients/client/ClientEditor.form'
import LoadingPanel from '@/components/loading-panel/LoadingPanel'
import { useFetcher } from 'react-router-dom'
import { isUserRoleAllowed } from '@/routes/utils'
import DocumentHead from '@/components/document-head/DocumentHead'
import { Form as DXForm, Tab, TabbedItem } from 'devextreme-react/form'
import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import { BlobSasPermissions } from '@/enums'
import AttachmentsManager from '@/components/file-manager/AttachmentsManager'
import { FileManagerRef } from 'devextreme-react/cjs/file-manager'
import { Roles } from '@/auth/azure/Roles'

const ClientEditor = (props: { creating: boolean }) => {
  const {
    clientData,
    clientId,
    defaultCRUDAllowedRoles,
    getUserInfo,
    agentHistoryGraphData,
    getGruppoAziendale,
    getRivenditore,
    getTipologiaRapporto,
    getAgente,
    getTipologiaPagamento,
    getContattiAziendali,
  } = useLoaderData<typeof clientEditorRouteLoader>()

  const fetcher = useFetcher()
  const title = clientData.data.nome ? `${clientData.data.nome} - Dettagli` : 'Nuovo cliente'
  const { client } = useQsAdminApiManager()
  const fileManagerRef = useRef<FileManagerRef>(null)

  return (
    <>
      <Suspense fallback={<LoadingPanel />}>
        <Await
          resolve={Promise.all([
            getGruppoAziendale,
            getRivenditore,
            getTipologiaRapporto,
            getAgente,
            getTipologiaPagamento,
            getContattiAziendali,
            getUserInfo,
          ])}
        >
          {([gruppiAziendali, rivenditori, tipiRapporto, agenti, tipiPagamento, contattiAziendali, userInfo]) => (
            <>
              <fetcher.Form>
                <ClientEditorForm
                  creating={props.creating}
                  agentHistoryGraph={agentHistoryGraphData}
                  gruppiAziendali={gruppiAziendali.data.value}
                  rivenditori={rivenditori.data.value}
                  tipiRapporto={tipiRapporto.data.value}
                  agenti={agenti.data.value}
                  tipiPagamento={tipiPagamento.data.value}
                  client={clientData.data}
                  clientId={clientId}
                  contattiAziendali={contattiAziendali?.data.value}
                  isRoleAllowed={isUserRoleAllowed(userInfo?.roles, defaultCRUDAllowedRoles)}
                  userInfo={userInfo}
                  fetcher={fetcher}
                />
              </fetcher.Form>
              {!props.creating && (
                <div id="allegati-company-container" className={'dx-card responsive-paddings'}>
                  <DXForm id="form-allegati">
                    <TabbedItem>
                      <Tab title="Allegati">
                        <AttachmentsManager
                          fileManagerRef={fileManagerRef}
                          storeInAzureBlobStorage={true}
                          requestUrl={`api/azienda`}
                          folderUrl={
                            isUserRoleAllowed(userInfo?.roles, [
                              Roles.Administrator,
                              Roles.GlobalAdministrator,
                              Roles.Supervisor,
                            ])
                              ? clientData.data.attachmentsFolderUrl
                              : isUserRoleAllowed(userInfo?.roles, [Roles.TechDeveloper])
                                ? `${clientData.data.attachmentsFolderUrl}/Tecnico`
                                : isUserRoleAllowed(userInfo?.roles, [Roles.Sales])
                                  ? `${clientData.data.attachmentsFolderUrl}/Commerciale`
                                  : isUserRoleAllowed(userInfo?.roles, [Roles.Marketing])
                                    ? `${clientData.data.attachmentsFolderUrl}/Marketing`
                                    : ''
                          }
                          folderUrlName={clientData.data.nome ?? clientData.data.ded_Dis}
                          preliminarPermissionsAction={async () => {
                            try {
                              const response = await client.post(
                                '/api/azienda/permissions',
                                {
                                  objectId: clientId,
                                  path: clientData.data.attachmentsFolderUrl,
                                },
                                {
                                  headers: {
                                    'Content-Type': 'application/json',
                                  },
                                },
                              )
                              return response.status === 200 ? (response.data as BlobSasPermissions[]) : []
                            } catch (error) {
                              console.log('Errore in recupero permessi', error)
                              return []
                            }
                          }}
                          createDirectoryAction={async (
                            rootFolderPath: string,
                            name: string,
                            conflictBerhavior: string,
                          ) => {
                            return await client.post(
                              '/api/azienda/createfolder',
                              {
                                rootFolder: rootFolderPath,
                                newFolderName: name,
                                objectId: clientId,
                                conflictBerhavior: conflictBerhavior,
                              },
                              {
                                headers: {
                                  'Content-Type': 'application/json',
                                },
                              },
                            )
                          }}
                        ></AttachmentsManager>
                      </Tab>
                    </TabbedItem>
                  </DXForm>
                </div>
              )}
            </>
          )}
        </Await>
      </Suspense>
    </>
  )
}

export default ClientEditor
